import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, ArrowRight, Globe, ChevronLeft, ChevronRight,
  LayoutGrid, X, Maximize2, Minimize2, BookOpen, Search, FileText, Check, ChevronDown,
} from "lucide-react";

// Public visual guide ("Guía Visual") — a slideshow tour of the portal, reachable
// from the landing page and the login screen without an account. Bilingual via the
// app's i18n language. One slide at a time: next/previous only, never a scroll.
// Mockups are stylized representations of the real screens, not screenshots.

type Lang = "es" | "en";
type Pair = readonly [string, string];
const P = (es: string, en: string): Pair => [es, en] as const;

function useTr() {
  const { i18n } = useTranslation();
  const lang: Lang = i18n.language.startsWith("en") ? "en" : "es";
  const t = (p: Pair) => (lang === "en" ? p[1] : p[0]);
  return { lang, t, i18n };
}

/* ---------- role colours (from tailwind.config.ts) ---------- */
const GOLD6 = "#a8853f";
const GOLD7 = "#866932";
const OCEAN5 = "#2f7896";
const NAVY7 = "#1e4d6b";
const TINT_GOLD = "#d4ae54";
const TINT_OCEAN = "#76b6cd";
const TINT_NAVY = "#90bbd7";
const TINT_BRONZE = "#dfc479";

/* ---------- screen-mockup primitives ---------- */

function Screen({ tab, title, children }: { tab: string; title: string; children: React.ReactNode }) {
  return (
    <div className="gvs-screen">
      <div className="gvs-bar">
        <span className="gvs-bmark" />
        <span className="gvs-bname">Costa Dorada I</span>
        <span className="gvs-btab">{tab}</span>
      </div>
      <div className="gvs-sbody">
        <div className="gvs-stitle">{title}</div>
        <div className="gvs-sstack">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, ph, select }: { label: string; value: string; ph?: boolean; select?: boolean }) {
  return (
    <div className="gvs-field">
      <div className="gvs-flabel">{label}</div>
      <div className={"gvs-fbox" + (ph ? " ph" : "")}>
        <span>{value}</span>
        {select && <ChevronDown size={17} className="gvs-chev" />}
      </div>
    </div>
  );
}

const Pair2 = ({ children }: { children: React.ReactNode }) => <div className="gvs-grid2">{children}</div>;

function Stat({ k, v, tone }: { k: string; v: string; tone?: "pos" | "neg" }) {
  return (
    <div className="gvs-stat">
      <div className="gvs-k">{k}</div>
      <div className={"gvs-v" + (tone ? " " + tone : "")}>{v}</div>
    </div>
  );
}
const Stats = ({ children }: { children: React.ReactNode }) => <div className="gvs-grid2">{children}</div>;

const Badge = ({ kind, children }: { kind: "warn" | "info" | "ok" | "danger" | "gold"; children: React.ReactNode }) => (
  <span className={"gvs-badge " + kind}>{children}</span>
);

const Btn = ({ kind, children }: { kind: "gold" | "sea" | "good" | "ghost" | "reject"; children: React.ReactNode }) => (
  <span className={"gvs-btn " + kind}>{children}</span>
);
const BtnRow = ({ children }: { children: React.ReactNode }) => <div className="gvs-btnrow">{children}</div>;

const Card = ({ children }: { children: React.ReactNode }) => <div className="gvs-card">{children}</div>;

function Rows({ items }: { items: Array<{ left: React.ReactNode; right: React.ReactNode }> }) {
  return (
    <div className="gvs-card">
      {items.map((r, i) => (
        <div className={"gvs-row" + (i === items.length - 1 ? " last" : "")} key={i}>
          {r.left}
          {r.right}
        </div>
      ))}
    </div>
  );
}
const RowLabel = ({ main, sub, alert }: { main: string; sub?: string; alert?: boolean }) => (
  <span className="gvs-rl">
    <span className="gvs-lbl">{main}</span>
    {sub && <span className={alert ? "gvs-recargo" : "gvs-sub"}>{sub}</span>}
  </span>
);
const RowRight = ({ num, children }: { num: string; children?: React.ReactNode }) => (
  <span className="gvs-rr">
    <span className="gvs-num">{num}</span>
    {children}
  </span>
);

function RulePanel({ count, title, rules }: { count?: number; title: string; rules: Array<{ title: string; body?: string; cite: string }> }) {
  return (
    <div className="gvs-rulep">
      <div className="gvs-rphead">
        <BookOpen size={20} />
        <span>{title}</span>
        {count != null && <span className="gvs-rpcount">{count}</span>}
      </div>
      {rules.map((r, i) => (
        <div className={"gvs-rprule" + (i ? " sep" : "")} key={i}>
          <div className="gvs-rptitle">{r.title}</div>
          {r.body && <div className="gvs-rpbody">{r.body}</div>}
          <span className="gvs-cite">{r.cite}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- slide model ---------- */

type Tr = (p: Pair) => string;

type Slide =
  | { kind: "cover"; title: Pair }
  | { kind: "roles"; title: Pair }
  | { kind: "divider"; num: string; section: Pair; title: Pair; intro: Pair; scenes: Pair[]; tint: string }
  | { kind: "scene"; section: Pair; color: string; step: Pair; title: Pair; body: Pair; tip?: { label: Pair; text: Pair }; mock: (t: Tr) => React.ReactNode }
  | { kind: "rules"; title: Pair }
  | { kind: "cta"; title: Pair };

const SEC_INICIO = P("Primeros pasos", "Getting started");
const SEC_RES = P("Residente / Titular", "Resident / Owner");
const SEC_JUNTA = P("Junta & Administración", "Board & Administration");
const SEC_CONT = P("Contable", "Bookkeeper");

const SLIDES: Slide[] = [
  { kind: "cover", title: P("El portal, escena por escena.", "The portal, scene by scene.") },
  { kind: "roles", title: P("Un solo sistema, tres formas de usarlo", "One system, three ways to use it") },

  {
    kind: "divider", num: "01", tint: TINT_GOLD,
    section: P("Para todos", "For everyone"),
    title: P("Primeros pasos", "Getting started"),
    intro: P(
      "Lo que todos ven al entrar, sin importar el rol. Tres pantallas que aparecen en cada sesión.",
      "What everyone sees on entry, whatever their role. Three screens present in every session."
    ),
    scenes: [P("Iniciar sesión", "Sign in"), P("El Panel", "The Dashboard"), P("El Asistente", "The Assistant")],
  },
  {
    kind: "scene", section: SEC_INICIO, color: GOLD6, step: P("Entrar", "Sign in"),
    title: P("Inicie sesión con su correo", "Sign in with your email"),
    body: P(
      "Cada persona tiene su propia cuenta. El menú lateral se arma automáticamente según su rol: un residente ve sus cuotas y permisos; la Junta ve gobernanza y finanzas.",
      "Each person has their own account. The sidebar builds itself from your role: a resident sees their fees and permits; the board sees governance and finances."
    ),
    tip: { label: P("Consejo:", "Tip:"), text: P("cambie el idioma en cualquier momento con el botón «Español / English».", "switch language anytime with the «Español / English» button.") },
    mock: (t) => (
      <Screen tab={t(P("Entrar", "Sign in"))} title={t(P("Bienvenido al Portal", "Welcome to the Portal"))}>
        <Card>
          <Field label={t(P("Correo", "Email"))} value="titular@costadorada1.com" />
          <Field label={t(P("Contraseña", "Password"))} value="••••••••••••" ph />
          <BtnRow>
            <Btn kind="gold">{t(P("Iniciar sesión", "Sign in"))}</Btn>
            <Btn kind="ghost">Español / English</Btn>
          </BtnRow>
        </Card>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_INICIO, color: GOLD6, step: P("Panel", "Dashboard"),
    title: P("El panel muestra lo que necesita atención", "The dashboard shows what needs attention"),
    body: P(
      "Al entrar verá tarjetas con lo pendiente: mantenimiento abierto, cuotas vencidas, permisos y resoluciones pendientes. Haga clic en una tarjeta para ir directo a esa sección.",
      "On entry you'll see cards for what's pending: open maintenance, overdue fees, permits and resolutions. Click a card to jump straight there."
    ),
    mock: (t) => (
      <Screen tab={t(P("Panel", "Dashboard"))} title={t(P("Panel", "Dashboard"))}>
        <Stats>
          <Stat k={t(P("Mantenimiento", "Maintenance"))} v="3" />
          <Stat k={t(P("Cuotas vencidas", "Overdue fees"))} v="3" tone="neg" />
          <Stat k={t(P("Permisos", "Permits"))} v="2" />
          <Stat k={t(P("Resoluciones", "Resolutions"))} v="1" />
        </Stats>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_INICIO, color: GOLD6, step: P("El Asistente", "The Assistant"),
    title: P("Pregunte y obtenga la respuesta — con la cita", "Ask, and get the answer — with the citation"),
    body: P(
      "El botón «Preguntar» está en todas las pantallas. Escriba su duda en español o inglés y el Asistente responde con los pasos a seguir y la regla que aplica, citando el documento oficial. Nadie tiene que leer 250 páginas.",
      "The «Ask» button is on every screen. Type your question in Spanish or English and the Assistant answers with the steps to follow and the rule that applies, citing the official document. Nobody has to read 250 pages."
    ),
    tip: { label: P("Pruebe:", "Try:"), text: P("«instalar rejas», «aviso para la asamblea», «recargo por atraso».", "«install security bars», «notice for the assembly», «late-payment surcharge».") },
    mock: (t) => (
      <Screen tab={t(P("Preguntar", "Ask"))} title={t(P("¿Cómo hago…?", "How do I…?"))}>
        <div className="gvs-search">
          <Search size={20} />
          <span className="gvs-q">{t(P("¿cuánto es la multa por violación?", "what's the fine for a violation?"))}</span>
        </div>
        <RulePanel
          title={t(P("Reglas y normas", "Rules & regulations"))}
          rules={[{
            title: t(P("Escala de multas por violación", "Fine schedule for violations")),
            body: t(P("1ª vez aviso; 2ª $50; 3ª $100; luego $100 por día.", "1st time notice; 2nd $50; 3rd $100; then $100 per day.")),
            cite: "Reglamento Art. VI Sec. 2(T)",
          }]}
        />
      </Screen>
    ),
  },

  {
    kind: "divider", num: "02", tint: TINT_OCEAN,
    section: SEC_RES,
    title: P("El día a día del titular", "The owner's day-to-day"),
    intro: P(
      "Todo lo que un dueño o residente puede hacer desde su cuenta: pagar, solicitar, reportar y mantenerse informado.",
      "Everything an owner or resident can do from their account: pay, request, report, and stay informed."
    ),
    scenes: [P("Mis Cuotas", "My Fees"), P("Solicitudes de Titulares", "Works permits"), P("Mantenimiento", "Maintenance"), P("Finanzas", "Finances"), P("Documentos", "Documents")],
  },
  {
    kind: "scene", section: SEC_RES, color: OCEAN5, step: P("Cuotas", "Fees"),
    title: P("Vea su balance y el recargo por atraso", "See your balance and the late charge"),
    body: P(
      "Las cuotas aparecen por mes. Tras 30 días de gracia, una cuota vencida acumula un recargo del 10%, que sube 1% adicional por cada mes que el atraso supere los 3 meses. El sistema lo calcula solo y usted descarga el recibo de cada pago.",
      "Fees appear by month. After a 30-day grace period, an overdue fee accrues a 10% late charge, rising 1% more for each month the delinquency exceeds 3 months. The system computes it for you, and you download the receipt for each payment."
    ),
    mock: (t) => (
      <Screen tab={t(P("Mis Cuotas", "My Fees"))} title={t(P("Mis Cuotas", "My Fees"))}>
        <Rows items={[
          {
            left: <RowLabel main={t(P("Julio 2026", "July 2026"))} sub={t(P("Cuota de mantenimiento", "Maintenance fee"))} />,
            right: <RowRight num="$285.00"><Badge kind="ok">{t(P("Pagada", "Paid"))}</Badge></RowRight>,
          },
          {
            left: <RowLabel main={t(P("Junio 2026", "June 2026"))} sub={t(P("+ $28.50 recargo (10%)", "+ $28.50 late fee (10%)"))} alert />,
            right: <RowRight num="$313.50"><Badge kind="danger">{t(P("Vencida", "Overdue"))}</Badge></RowRight>,
          },
        ]} />
        <BtnRow><Btn kind="sea">{t(P("Descargar recibo", "Download receipt"))}</Btn></BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_RES, color: OCEAN5, step: P("Permiso de obra", "Works permit"),
    title: P("Solicite una obra y vea las reglas al instante", "Request work and see the rules instantly"),
    body: P(
      "Al pedir permiso para una remodelación, el formulario muestra un panel «Reglas aplicables» con lo que exige el Manual: la fianza, el horario de construcción y las especificaciones. La Junta decide con la misma información al frente.",
      "When you request a remodel permit, the form shows an «Applicable rules» panel with what the Manual requires: the bond, construction hours, and the specs. The board decides with the same information in front of it."
    ),
    tip: { label: P("Protección legal:", "Legal protection:"), text: P("cada decisión queda respaldada por su artículo del Manual o Reglamento.", "every decision is backed by its article of the Manual or Reglamento.") },
    mock: (t) => (
      <Screen tab={t(P("Solicitudes de Titulares", "Works Permits"))} title={t(P("Nueva solicitud", "New request"))}>
        <Field label={t(P("Tipo de obra", "Type of work"))} value={t(P("Instalación de rejas", "Security-bar installation"))} select />
        <RulePanel count={12} title={t(P("Reglas aplicables", "Applicable rules"))} rules={[
          { title: t(P("Fianza (depósito) de obra: $1,000", "Works bond (deposit): $1,000")), cite: "Manual §6.4" },
          { title: t(P("Horario: L–V 8:00–17:00", "Hours: Mon–Fri 8:00–17:00")), cite: "Manual §6.6" },
          { title: t(P('Rejas: blancas, barras 1" a 4"', 'Bars: white, 1" bars 4" apart')), cite: "Manual §7" },
        ]} />
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_RES, color: OCEAN5, step: P("Mantenimiento", "Maintenance"),
    title: P("Reporte una avería y siga su estado", "Report a repair and track its status"),
    body: P(
      "Describa el problema y la ubicación. La administración lo recibe, lo asigna y actualiza el estado (abierto → en progreso → resuelto). Usted ve el avance sin tener que llamar.",
      "Describe the problem and location. The administration receives it, assigns it, and updates the status (open → in progress → resolved). You see the progress without having to call."
    ),
    mock: (t) => (
      <Screen tab={t(P("Mantenimiento", "Maintenance"))} title={t(P("Reportar mantenimiento", "Report maintenance"))}>
        <Pair2>
          <Field label={t(P("Categoría", "Category"))} value={t(P("Plomería", "Plumbing"))} select />
          <Field label={t(P("Ubicación", "Location"))} value={t(P("Área común · piscina", "Common area · pool"))} />
        </Pair2>
        <Field label={t(P("Descripción", "Description"))} value={t(P("Fuga en la tubería junto a…", "Leak in the pipe next to…"))} ph />
        <BtnRow><Btn kind="gold">{t(P("Enviar", "Submit"))}</Btn></BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_RES, color: OCEAN5, step: P("Transparencia", "Transparency"),
    title: P("Las finanzas del condominio, a la vista", "The condominium's finances, in plain view"),
    body: P(
      "Cada mes la administración publica un resumen: ingresos, gastos, resultado y fondo de reserva, con el informe en PDF para descargar. Los titulares ven las finanzas sin tener que pedirlas.",
      "Each month the administration publishes a summary: income, expenses, result, and reserve fund, with the report in PDF to download. Owners see the finances without having to ask."
    ),
    mock: (t) => (
      <Screen tab={t(P("Finanzas", "Finances"))} title={t(P("Finanzas del Condominio", "Condominium Finances"))}>
        <Stats>
          <Stat k={t(P("Ingresos", "Income"))} v="$24,850" />
          <Stat k={t(P("Gastos", "Expenses"))} v="$19,320" />
          <Stat k={t(P("Resultado", "Result"))} v="+$5,530" tone="pos" />
          <Stat k={t(P("Reserva", "Reserve"))} v="$61,400" />
        </Stats>
        <BtnRow><Btn kind="sea">{t(P("Descargar informe (PDF)", "Download report (PDF)"))}</Btn></BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_RES, color: OCEAN5, step: P("Documentos", "Documents"),
    title: P("Lea los documentos que rigen el condominio", "Read the documents that govern the condominium"),
    body: P(
      "El Reglamento, el Manual de Normas, la Escritura Matriz y los planes de emergencia y racionamiento están disponibles para los titulares en todo momento — siempre la versión vigente.",
      "The Bylaws, Rules Manual, Master Deed, and the emergency and rationing plans are available to owners at all times — always the current version."
    ),
    mock: (t) => (
      <Screen tab={t(P("Documentos", "Documents"))} title={t(P("Biblioteca de Documentos", "Document Library"))}>
        <Rows items={[
          { left: <span className="gvs-doc"><FileText size={19} />{t(P("Reglamento", "Bylaws"))}</span>, right: <Badge kind="info">v2.1</Badge> },
          { left: <span className="gvs-doc"><FileText size={19} />{t(P("Manual de Normas", "Rules Manual"))}</span>, right: <Badge kind="info">v1.3</Badge> },
          { left: <span className="gvs-doc"><FileText size={19} />{t(P("Escritura Matriz", "Master Deed"))}</span>, right: <Badge kind="gold">{t(P("Registrada", "Registered"))}</Badge> },
          { left: <span className="gvs-doc"><FileText size={19} />{t(P("Plan de Emergencias", "Emergency Plan"))}</span>, right: <Badge kind="info">2026</Badge> },
        ]} />
      </Screen>
    ),
  },

  {
    kind: "divider", num: "03", tint: TINT_NAVY,
    section: SEC_JUNTA,
    title: P("Gobernar y administrar", "Govern and administer"),
    intro: P(
      "Las decisiones de la Junta, respaldadas por las reglas del condominio en el momento exacto en que se toman.",
      "The board's decisions, backed by the condominium's rules at the exact moment they're made."
    ),
    scenes: [P("Comunicados", "Announcements"), P("Reuniones", "Meetings"), P("Aprobaciones", "Approvals"), P("Incidentes", "Incidents"), P("Resoluciones", "Resolutions"), P("Unidades", "Units")],
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Comunicados", "Announcements"),
    title: P("Publique avisos a todos los titulares", "Post notices to all owners"),
    body: P(
      "Redacte el comunicado, elija una categoría y publíquelo — se envía por correo a todos. En los avisos «Reglamentarios» aparecen las normas para citarlas correctamente en el texto.",
      "Write the announcement, pick a category, and publish it — it's emailed to everyone. On «Regulatory» notices the rules appear so you can cite them correctly in the text."
    ),
    mock: (t) => (
      <Screen tab={t(P("Comunicados", "Announcements"))} title={t(P("Nuevo comunicado", "New announcement"))}>
        <Pair2>
          <Field label={t(P("Categoría", "Category"))} value={t(P("Reglamentario", "Regulatory"))} select />
          <Field label={t(P("Vence", "Expires"))} value={t(P("— opcional —", "— optional —"))} ph />
        </Pair2>
        <RulePanel count={8} title={t(P("Reglas aplicables", "Applicable rules"))} rules={[
          { title: t(P("Escala de multas · normas de convivencia", "Fine schedule · community rules")), cite: "Reglamento Art. VI · Manual §2" },
        ]} />
        <BtnRow>
          <Btn kind="gold">{t(P("Guardar borrador", "Save draft"))}</Btn>
          <Btn kind="sea">{t(P("Publicar y enviar", "Publish and send"))}</Btn>
        </BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Reuniones", "Meetings"),
    title: P("Convoque una reunión o asamblea", "Call a meeting or assembly"),
    body: P(
      "Al agendar, el sistema recuerda los plazos: convocatoria con 15 días para la asamblea ordinaria (5 para la extraordinaria), quórum de mitad más uno, y los actos que requieren 2/3 de participación. Menos errores, más protección.",
      "When scheduling, the system reminds you of the deadlines: 15 days' notice for the ordinary assembly (5 for the extraordinary one), a half-plus-one quorum, and the acts requiring 2/3 of participation. Fewer errors, more protection."
    ),
    mock: (t) => (
      <Screen tab={t(P("Reuniones", "Meetings"))} title={t(P("Nueva reunión", "New meeting"))}>
        <Pair2>
          <Field label={t(P("Tipo", "Type"))} value={t(P("Asamblea", "Assembly"))} select />
          <Field label={t(P("Fecha", "Date"))} value={t(P("15 nov 2026", "Nov 15, 2026"))} />
        </Pair2>
        <RulePanel count={3} title={t(P("Reglas aplicables", "Applicable rules"))} rules={[
          { title: t(P("Convocatoria: 15 días (ordinaria)", "Notice: 15 days (ordinary)")), cite: "Reglamento Art. II" },
          { title: t(P("Quórum ½+1 · actos especiales 2/3", "Quorum ½+1 · special acts 2/3")), cite: "Reglamento Art. II" },
        ]} />
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Aprobaciones", "Approvals"),
    title: P("Apruebe con la norma al frente", "Approve with the rule in front of you"),
    body: P(
      "La Junta revisa cada solicitud con las reglas visibles y añade condiciones. Al aprobar, el sistema genera automáticamente la Resolución numerada en PDF — el registro formal de la decisión.",
      "The board reviews each request with the rules visible and adds conditions. On approval, the system automatically generates the numbered Resolution PDF — the formal record of the decision."
    ),
    mock: (t) => (
      <Screen tab={t(P("Revisar permiso", "Review permit"))} title={t(P("Revisar — Instalación de rejas", "Review — Security-bar installation"))}>
        <Card>
          <div className="gvs-kv"><span className="gvs-k">{t(P("Unidad", "Unit"))}</span><span className="gvs-lbl">J5 · Pedro Rivera</span></div>
          <div className="gvs-kv"><span className="gvs-k">{t(P("Estado", "Status"))}</span><Badge kind="warn">{t(P("En revisión", "Under review"))}</Badge></div>
        </Card>
        <RulePanel count={12} title={t(P("Reglas aplicables", "Applicable rules"))} rules={[
          { title: t(P('Rejas blancas, barras 1" a 4"', 'White bars, 1" bars 4" apart')), cite: "Manual §7" },
        ]} />
        <BtnRow>
          <Btn kind="reject">{t(P("Rechazar", "Reject"))}</Btn>
          <Btn kind="good">{t(P("Aprobar", "Approve"))}</Btn>
        </BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Incidentes", "Incidents"),
    title: P("Registre una violación con su escala de multas", "Log a violation with its fine schedule"),
    body: P(
      "Cuando el tipo es «Violación de reglamento», aparece la escala oficial (aviso → $50 → $100 → $100/día) para actuar de forma consistente y documentada. Todo queda en el historial.",
      "When the type is «Rule violation», the official schedule appears (notice → $50 → $100 → $100/day) so you act consistently and on the record. Everything is kept in the history."
    ),
    mock: (t) => (
      <Screen tab={t(P("Incidentes", "Incidents"))} title={t(P("Nuevo incidente", "New incident"))}>
        <Field label={t(P("Tipo", "Type"))} value={t(P("Violación de reglamento", "Rule violation"))} select />
        <RulePanel count={2} title={t(P("Reglas aplicables", "Applicable rules"))} rules={[
          { title: t(P("1ª aviso · 2ª $50 · 3ª $100 · luego $100/día", "1st notice · 2nd $50 · 3rd $100 · then $100/day")), cite: "Reglamento Art. VI Sec. 2(T)" },
        ]} />
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Resoluciones", "Resolutions"),
    title: P("Cree resoluciones y registre los votos", "Create resolutions and record votes"),
    body: P(
      "Cada miembro de la Junta emite su voto en el sistema; el conteo se lleva solo. Al cerrarse, la resolución queda aprobada o rechazada con su registro y su PDF.",
      "Each board member casts their vote in the system; the tally is kept automatically. On close, the resolution is approved or rejected with its record and its PDF."
    ),
    mock: (t) => (
      <Screen tab={t(P("Resoluciones", "Resolutions"))} title="Resolución 2026-014">
        <Card>
          <div className="gvs-restitle">{t(P("Contratación de repavimentación", "Repaving contract"))}</div>
          <div className="gvs-sub">{t(P("Propuesta por la Junta · en votación", "Proposed by the board · in voting"))}</div>
          <div className="gvs-tally">
            <span className="y">{t(P("4 Sí", "4 Yes"))}</span>
            <span className="n">1 No</span>
            <span className="a">{t(P("0 Abst.", "0 Abst."))}</span>
          </div>
          <BtnRow>
            <Btn kind="good">{t(P("Sí", "Yes"))}</Btn>
            <Btn kind="ghost">No</Btn>
            <Btn kind="ghost">{t(P("Abstención", "Abstain"))}</Btn>
          </BtnRow>
        </Card>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_JUNTA, color: NAVY7, step: P("Unidades", "Units"),
    title: P("El registro de titulares y su cuota", "The owner registry and its fee"),
    body: P(
      "Cada apartamento tiene su titular, su dirección USPS y su cuota de mantenimiento mensual. Esa cuota es la que se factura cada mes, y el sistema avisa cuántas unidades aún no la tienen.",
      "Each apartment has its owner, its USPS address and its monthly maintenance fee. That fee is what gets billed each month, and the system flags how many units don't have one yet."
    ),
    mock: (t) => (
      <Screen tab={t(P("Unidades", "Units"))} title={t(P("Unidades", "Units"))}>
        <Rows items={[
          { left: <RowLabel main={t(P("Apt. J5 · 3 hab.", "Apt. J5 · 3 bd"))} />, right: <RowRight num="$285.00" /> },
          { left: <RowLabel main={t(P("Apt. M3 · 2 hab.", "Apt. M3 · 2 bd"))} />, right: <RowRight num="$245.00" /> },
          { left: <RowLabel main={t(P("Apt. O8 · 3 hab.", "Apt. O8 · 3 bd"))} />, right: <RowRight num="$285.00" /> },
        ]} />
        <div className="gvs-total"><Check size={19} />{t(P("Todas las unidades tienen cuota", "Every unit has a fee"))}</div>
      </Screen>
    ),
  },

  {
    kind: "divider", num: "04", tint: TINT_BRONZE,
    section: SEC_CONT,
    title: P("Llevar los libros, sin duplicar", "Keep the books, without duplication"),
    intro: P(
      "El contable trabaja en QuickBooks; el portal es la capa de cobro y transparencia. Un acceso especial abre solo lo necesario.",
      "The bookkeeper works in QuickBooks; the portal is the collections and transparency layer. A special access opens only what's needed."
    ),
    scenes: [P("Gastos", "Expenses"), P("Generar cuotas", "Generate fees"), P("Cierre mensual", "Monthly close")],
  },
  {
    kind: "scene", section: SEC_CONT, color: GOLD7, step: P("Gastos", "Expenses"),
    title: P("Registre gastos con el mismo catálogo de cuentas", "Record expenses with the same chart of accounts"),
    body: P(
      "Las categorías coinciden con el catálogo de cuentas de QuickBooks (Nómina, Servicios, Mantenimiento…), con su código. Así los números del portal y de la contabilidad hablan el mismo idioma.",
      "The categories match the QuickBooks chart of accounts (Payroll, Services, Maintenance…), with their code. So the portal's numbers and the bookkeeping speak the same language."
    ),
    mock: (t) => (
      <Screen tab={t(P("Gastos", "Expenses"))} title={t(P("Nuevo gasto", "New expense"))}>
        <Field label={t(P("Categoría", "Category"))} value={t(P("560 · Mantenimiento de áreas comunes", "560 · Maintenance – common areas"))} select />
        <Pair2>
          <Field label={t(P("Monto", "Amount"))} value="$1,240.00" />
          <Field label={t(P("Proveedor", "Vendor"))} value="Jardinería del Norte" />
        </Pair2>
        <BtnRow><Btn kind="gold">{t(P("Guardar", "Save"))}</Btn></BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_CONT, color: GOLD7, step: P("Cuotas", "Fees"),
    title: P("Genere las cuotas del mes de una sola vez", "Generate the month's fees in one step"),
    body: P(
      "Elija el período y la fecha de vencimiento; el sistema factura a cada unidad su cuota mensual y le avisa cuáles no tienen cuota asignada. Las que se atrasen muestran el recargo automáticamente, y puede enviar recordatorios por correo.",
      "Pick the period and the due date; the system bills each unit its monthly fee and tells you which ones have no fee set. Those that fall behind show the late charge automatically, and you can send email reminders."
    ),
    mock: (t) => (
      <Screen tab={t(P("Cuotas", "Fees"))} title={t(P("Generar cuotas", "Generate fees"))}>
        <Pair2>
          <Field label={t(P("Período", "Period"))} value="2026-08" />
          <Field label={t(P("Monto total a distribuir", "Total amount to distribute"))} value="$30,780.00" />
        </Pair2>
        <BtnRow><Btn kind="sea">{t(P("Generar para 108 unidades", "Generate for 108 units"))}</Btn></BtnRow>
      </Screen>
    ),
  },
  {
    kind: "scene", section: SEC_CONT, color: GOLD7, step: P("Cierre mensual", "Monthly close"),
    title: P("Cierre el mes en una sola pantalla", "Close the month on one screen"),
    body: P(
      "Elija el período, suba el P&L en PDF, escriba unas pocas cifras clave y presione «Publicar». El resultado neto se calcula solo, y con un clic queda visible para todos los titulares en su página de finanzas.",
      "Pick the period, upload the P&L PDF, enter a few key figures, and press «Publish». The net result computes itself, and with one click it's visible to all owners on their finances page."
    ),
    tip: { label: P("Año fiscal:", "Fiscal year:"), text: P("el portal maneja el ciclo agosto–julio del condominio.", "the portal handles the condominium's August–July cycle.") },
    mock: (t) => (
      <Screen tab={t(P("Cierre Mensual", "Monthly Close"))} title={t(P("Cierre mensual · Julio 2026", "Monthly close · July 2026"))}>
        <div className="gvs-upload"><FileText size={19} />{t(P("Informe P&L (PDF) — subir o desde Google Drive", "P&L report (PDF) — upload or from Google Drive"))}</div>
        <Pair2>
          <Field label={t(P("Ingresos", "Income"))} value="$24,850" />
          <Field label={t(P("Gastos", "Expenses"))} value="$19,320" />
        </Pair2>
        <div className="gvs-kv"><span className="gvs-k">{t(P("Resultado neto", "Net result"))}</span><span className="gvs-net">+$5,530</span></div>
        <BtnRow>
          <Btn kind="gold">{t(P("Guardar borrador", "Save draft"))}</Btn>
          <Btn kind="sea">{t(P("Publicar", "Publish"))}</Btn>
        </BtnRow>
      </Screen>
    ),
  },

  { kind: "rules", title: P("El portal conoce sus reglas", "The portal knows its rules") },
  { kind: "cta", title: P("Entre al portal", "Enter the portal") },
];

const TOTAL = SLIDES.length;

/* ---------- slide frames ---------- */

const BRAND = <span className="gvs-mark">CD</span>;

function Chrome({ section, color, n, light }: { section: string; color: string; n: number; light?: boolean }) {
  return (
    <div className={"gvs-chrome" + (light ? " light" : "")}>
      {BRAND}
      <span className="gvs-brandname">Condominio Costa Dorada&nbsp;I</span>
      <span className="gvs-chrome-r">
        <span className="gvs-section" style={{ color }}>{section}</span>
        <span className="gvs-count">{String(n).padStart(2, "0")} / {TOTAL}</span>
      </span>
    </div>
  );
}

function SlideView({ slide, n, t }: { slide: Slide; n: number; t: Tr }) {
  if (slide.kind === "cover") {
    return (
      <div className="gvs-frame navy cover">
        <div className="gvs-coverin">
          <div className="gvs-brandline">{BRAND}<span className="gvs-brandname big">Condominio Costa Dorada&nbsp;I</span></div>
          <span className="gvs-eyebrow gold">{t(P("Portal de Gestión · Guía visual", "Management Portal · Visual guide"))}</span>
          <h1>{t(slide.title)}</h1>
          <p className="gvs-lede">
            {t(P(
              "Un recorrido por el sistema de gestión del condominio: qué hace cada pantalla, quién la usa y cómo fluye el trabajo del día a día — desde pagar una cuota hasta cerrar el mes o aprobar una obra.",
              "A tour of the condominium's management system: what each screen does, who uses it, and how the day-to-day work flows — from paying a fee to closing the month or approving construction."
            ))}
          </p>
          <div className="gvs-legend">
            <span className="gvs-role"><i style={{ background: TINT_OCEAN }} />{t(SEC_RES)}</span>
            <span className="gvs-role"><i style={{ background: TINT_NAVY }} />{t(SEC_JUNTA)}</span>
            <span className="gvs-role"><i style={{ background: TINT_BRONZE }} />{t(SEC_CONT)}</span>
          </div>
        </div>
        <div className="gvs-coverfoot">
          <span>Dorado, Puerto Rico</span><span className="gvs-dot">·</span>
          <span>{t(P("Bilingüe (Español / English)", "Bilingual (Español / English)"))}</span>
        </div>
      </div>
    );
  }

  if (slide.kind === "roles") {
    const cards: Array<{ c: string; title: Pair; lines: Pair[] }> = [
      { c: OCEAN5, title: SEC_RES, lines: [
        P("Sus cuotas, recibos y recargos", "Their fees, receipts and late charges"),
        P("Solicitudes de Titulares y averías", "Works permits and repairs"),
        P("Finanzas y documentos del condominio", "Condominium finances and documents")] },
      { c: NAVY7, title: SEC_JUNTA, lines: [
        P("Comunicados, reuniones y actas", "Announcements, meetings and minutes"),
        P("Permisos, incidentes y resoluciones", "Permits, incidents and resolutions"),
        P("Registro de titulares y cuotas", "Owner registry and fees")] },
      { c: GOLD7, title: SEC_CONT, lines: [
        P("Gastos con el catálogo de cuentas", "Expenses with the chart of accounts"),
        P("Generación de cuotas del mes", "Generating the month's fees"),
        P("Cierre mensual e informe P&L", "Monthly close and P&L report")] },
    ];
    return (
      <div className="gvs-frame sand">
        <Chrome section={t(P("El portal", "The portal"))} color={GOLD6} n={n} />
        <div className="gvs-rolesbody">
          <div className="gvs-rolehead">
            <h2>{t(slide.title)}</h2>
            <p>{t(P(
              "Cada persona entra con su propia cuenta y el portal muestra únicamente lo que corresponde a su rol. El menú se arma solo: nadie ve pantallas que no le tocan.",
              "Each person signs in with their own account and the portal shows only what matches their role. The menu builds itself: nobody sees screens that aren't theirs."
            ))}</p>
          </div>
          <div className="gvs-rolegrid">
            {cards.map((c, i) => (
              <div className="gvs-rolecard" key={i}>
                <span className="gvs-rolebar" style={{ background: c.c }} />
                <h3>{t(c.title)}</h3>
                <div className="gvs-rolelines">
                  {c.lines.map((l, j) => (
                    <div className="gvs-roleline" key={j}><Check size={18} style={{ color: c.c }} />{t(l)}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="gvs-tip"><b>{t(P("Bilingüe:", "Bilingual:"))}</b> {t(P(
            "todo el portal funciona en español e inglés, y se cambia de idioma en cualquier momento.",
            "the whole portal works in Spanish and English, and you can switch language at any time."
          ))}</div>
        </div>
      </div>
    );
  }

  if (slide.kind === "divider") {
    return (
      <div className="gvs-frame navy">
        <Chrome section={t(slide.section)} color={slide.tint} n={n} light />
        <div className="gvs-divbody">
          <div className="gvs-divtext">
            <span className="gvs-eyebrow" style={{ color: slide.tint }}>{t(slide.section)}</span>
            <h2>{t(slide.title)}</h2>
            <p>{t(slide.intro)}</p>
            <div className="gvs-scenes">
              {slide.scenes.map((s, i) => (
                <span className="gvs-scene" key={i}><i style={{ background: slide.tint }} />{t(s)}</span>
              ))}
            </div>
          </div>
          <div className="gvs-divnum">{slide.num}</div>
        </div>
      </div>
    );
  }

  if (slide.kind === "rules") {
    const steps: Array<[string, Pair, Pair]> = [
      ["01", P("Documentos", "Documents"), P("Escritura, Reglamento, Manual y planes de emergencia — en la biblioteca, siempre vigentes.", "Deed, Bylaws, Manual, and emergency plans — in the library, always current.")],
      ["02", P("Reglas con cita", "Cited rules"), P("Sus normas operativas quedan resumidas, con la cita de su artículo de origen.", "Their operative rules are summarized, with the citation of their source article.")],
      ["03", P("En cada decisión", "At every decision"), P("Aparecen por sí solas en permisos, incidentes, cuotas, reuniones y resoluciones.", "They appear on their own in permits, incidents, fees, meetings, and resolutions.")],
      ["04", P("Y a la mano", "And on hand"), P("El Asistente «Preguntar» responde cualquier duda con la regla y su fuente.", "The «Ask» Assistant answers any question with the rule and its source.")],
    ];
    return (
      <div className="gvs-frame navy">
        <Chrome section={t(P("La inteligencia del sistema", "The system's intelligence"))} color={TINT_GOLD} n={n} light />
        <div className="gvs-rulesbody">
          <div className="gvs-ruleshead">
            <h2>{t(slide.title)}</h2>
            <p>{t(P(
              "Los documentos que rigen Costa Dorada I no viven olvidados en un PDF: sus reglas están dentro del sistema y aparecen justo donde se toman las decisiones. Así la administración actúa con rapidez y con respaldo, y nadie tiene que releer el Reglamento entero.",
              "The documents that govern Costa Dorada I don't sit forgotten in a PDF: their rules live inside the system and appear right where decisions are made. The administration acts quickly and with backing, and nobody has to re-read the whole Reglamento."
            ))}</p>
          </div>
          <div className="gvs-flow">
            {steps.map(([num, ti, bo]) => (
              <div className="gvs-fstep" key={num}>
                <span className="gvs-fn">{num}</span>
                <h4>{t(ti)}</h4>
                <p>{t(bo)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (slide.kind === "cta") {
    const steps: Array<[string, Pair, Pair]> = [
      ["1", P("Reciba sus credenciales", "Receive your credentials"), P("La administración le envía por correo su usuario y contraseña inicial.", "The administration emails you your username and initial password.")],
      ["2", P("Cambie su contraseña", "Change your password"), P("En el primer inicio de sesión, elija una contraseña propia.", "On first sign-in, choose a password of your own.")],
      ["3", P("Elija su idioma", "Choose your language"), P("Español o inglés, y cámbielo cuando quiera desde cualquier pantalla.", "Spanish or English, switchable at any time from any screen.")],
      ["4", P("Empiece por el Panel", "Start at the Dashboard"), P("Ahí verá lo que necesita su atención, y desde ahí llegará a todo lo demás.", "There you'll see what needs your attention, and from there you reach everything else.")],
    ];
    return (
      <div className="gvs-frame navy">
        <Chrome section={t(P("Para empezar", "To begin"))} color={TINT_GOLD} n={n} light />
        <div className="gvs-ctabody">
          <div className="gvs-ctaleft">
            <span className="gvs-eyebrow gold">{t(P("Portal de Gestión", "Management Portal"))}</span>
            <h2>{t(slide.title)}</h2>
            <p>{t(P(
              "Desde cualquier navegador, en el teléfono o en la computadora. No hay nada que instalar.",
              "From any browser, on your phone or your computer. There is nothing to install."
            ))}</p>
            <Link to="/login" className="gvs-ctabtn">
              {t(P("Entrar al portal", "Enter the portal"))}<ArrowRight size={20} />
            </Link>
          </div>
          <div className="gvs-ctasteps">
            {steps.map(([num, ti, bo]) => (
              <div className="gvs-ctastep" key={num}>
                <span className="gvs-ctan">{num}</span>
                <span className="gvs-ctatext"><b>{t(ti)}</b><span>{t(bo)}</span></span>
              </div>
            ))}
          </div>
        </div>
        <div className="gvs-coverfoot">
          <span>{t(P("Asociación Condominio Costa Dorada I · Dorado, Puerto Rico", "Costa Dorada I Condominium Association · Dorado, Puerto Rico"))}</span>
        </div>
      </div>
    );
  }

  // scene
  return (
    <div className="gvs-frame sand">
      <Chrome section={t(slide.section)} color={slide.color} n={n} />
      <div className="gvs-scenebody">
        <div className="gvs-scenetext">
          <span className="gvs-eyebrow" style={{ color: slide.color }}>{t(slide.step)}</span>
          <h2>{t(slide.title)}</h2>
          <p>{t(slide.body)}</p>
          {slide.tip && <div className="gvs-tip"><b>{t(slide.tip.label)}</b> {t(slide.tip.text)}</div>}
        </div>
        <div className="gvs-scenemock">{slide.mock(t)}</div>
      </div>
      <div className="gvs-progress"><span style={{ width: `${(n / TOTAL) * 100}%`, background: slide.color }} /></div>
    </div>
  );
}

/* ---------- page ---------- */

export function GuiaVisualPage() {
  const { t, i18n, lang } = useTr();
  const isEn = lang === "en";
  const [i, setI] = useState(() => {
    const n = parseInt(window.location.hash.replace("#", ""), 10);
    return Number.isFinite(n) ? Math.max(0, Math.min(TOTAL - 1, n - 1)) : 0;
  });
  const [scale, setScale] = useState(1);
  const [narrow, setNarrow] = useState(false);
  const [showIndex, setShowIndex] = useState(false);
  const [full, setFull] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const go = useCallback((n: number) => setI((p) => Math.max(0, Math.min(TOTAL - 1, typeof n === "number" ? n : p))), []);
  const next = useCallback(() => setI((p) => Math.min(TOTAL - 1, p + 1)), []);
  const prev = useCallback(() => setI((p) => Math.max(0, p - 1)), []);

  useEffect(() => {
    document.title = t(P("Guía Visual — Costa Dorada I", "Visual Guide — Costa Dorada I"));
  }, [isEn]); // eslint-disable-line react-hooks/exhaustive-deps

  // fit the 1440×810 slide into the viewport on wide screens
  useEffect(() => {
    const fit = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isNarrow = w < 900;
      setNarrow(isNarrow);
      setScale(isNarrow ? 1 : Math.min(w / 1440, h / 810));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  // the slide number lives in the URL, so a slide can be linked and a reload keeps your place
  useEffect(() => {
    try { window.history.replaceState(null, "", `#${i + 1}`); } catch { /* history unavailable */ }
  }, [i]);

  // a pasted or edited #n in the address bar moves the deck
  useEffect(() => {
    const onHash = () => {
      const n = parseInt(window.location.hash.replace("#", ""), 10);
      if (Number.isFinite(n)) go(n - 1);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [go]);

  // lock the page: this is a deck, not a scrolling document
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      if (k === "Escape") { setShowIndex(false); return; }
      if (k === "ArrowRight" || k === "PageDown" || k === " ") { e.preventDefault(); next(); }
      else if (k === "ArrowLeft" || k === "PageUp") { e.preventDefault(); prev(); }
      else if (k === "Home") { e.preventDefault(); go(0); }
      else if (k === "End") { e.preventDefault(); go(TOTAL - 1); }
      else if (k === "g" || k === "G") { setShowIndex((s) => !s); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go]);

  useEffect(() => {
    const onFs = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const toggleFull = () => {
    try {
      if (document.fullscreenElement) document.exitFullscreen();
      else rootRef.current?.requestFullscreen?.();
    } catch { /* fullscreen unavailable */ }
  };

  // swipe, for phones and tablets
  const touch = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    touch.current = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 46 && Math.abs(dx) > Math.abs(dy)) (dx < 0 ? next : prev)();
    touch.current = null;
  };

  const titles = useMemo(
    () => SLIDES.map((s, idx) => {
      if (s.kind === "cover") return t(P("Portada", "Cover"));
      if (s.kind === "roles") return t(P("Un sistema, tres roles", "One system, three roles"));
      if (s.kind === "divider") return t(s.title);
      if (s.kind === "rules") return t(s.title);
      if (s.kind === "cta") return t(s.title);
      return t(s.step) + " · " + t(s.section).split(" ")[0] + (idx < 0 ? "" : "");
    }),
    [isEn] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tone = (idx: number) => {
    const s = SLIDES[idx];
    if (s.kind === "scene") return s.color;
    if (s.kind === "divider") return s.tint;
    return GOLD6;
  };

  return (
    <div className={"gvs-root" + (narrow ? " narrow" : "")} ref={rootRef}>
      <style>{CSS}</style>

      <div className="gvs-stage" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="gvs-deck" style={narrow ? undefined : { transform: `scale(${scale})` }}>
          {SLIDES.map((s, idx) => (
            <div className={"gvs-slide" + (idx === i ? " on" : "")} key={idx} aria-hidden={idx !== i}>
              <SlideView slide={s} n={idx + 1} t={t} />
            </div>
          ))}
        </div>
      </div>

      <div className="gvs-rail"><span style={{ width: `${((i + 1) / TOTAL) * 100}%` }} /></div>

      <div className="gvs-top">
        <Link to="/" className="gvs-pillbtn"><ArrowLeft size={15} />{t(P("Inicio", "Home"))}</Link>
        <button type="button" className="gvs-pillbtn" onClick={() => i18n.changeLanguage(isEn ? "es" : "en")}>
          <Globe size={15} />{isEn ? "Español" : "English"}
        </button>
      </div>

      <nav className="gvs-ctl" aria-label={t(P("Controles de la presentación", "Presentation controls"))}>
        <button type="button" onClick={() => setShowIndex(true)} aria-label={t(P("Ver todas las láminas", "See all slides"))} title="G">
          <LayoutGrid size={19} />
        </button>
        <span className="gvs-sep" />
        <button type="button" onClick={prev} disabled={i === 0} aria-label={t(P("Lámina anterior", "Previous slide"))}>
          <ChevronLeft size={20} />
        </button>
        <span className="gvs-ctlcount" aria-live="polite">{String(i + 1).padStart(2, "0")} / {TOTAL}</span>
        <button type="button" onClick={next} disabled={i === TOTAL - 1} aria-label={t(P("Lámina siguiente", "Next slide"))}>
          <ChevronRight size={20} />
        </button>
        <span className="gvs-sep" />
        <button type="button" onClick={toggleFull} aria-label={t(P("Pantalla completa", "Fullscreen"))}>
          {full ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </nav>

      {showIndex && (
        <div className="gvs-index" role="dialog" aria-modal="true">
          <div className="gvs-indexhead">
            <h2>{t(P("Todas las láminas", "All slides"))}</h2>
            <button type="button" onClick={() => setShowIndex(false)} aria-label={t(P("Cerrar", "Close"))}><X size={20} /></button>
          </div>
          <div className="gvs-indexgrid">
            {titles.map((ti, idx) => (
              <button
                type="button"
                key={idx}
                className={"gvs-ix" + (idx === i ? " here" : "")}
                onClick={() => { go(idx); setShowIndex(false); }}
              >
                <i style={{ background: tone(idx) }} />
                <span className="gvs-ixn">{String(idx + 1).padStart(2, "0")}</span>
                <span className="gvs-ixt">{ti}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- scoped styles (app palette from tailwind.config.ts) ---------- */

const CSS = `
.gvs-root {
  --navy8:#1c3f55; --navy7:#1e4d6b; --navy6:#2e6489; --navy950:#0c1e2c;
  --gold:#c8a560; --gold4:#d4ae54; --gold6:#a8853f; --gold7:#866932;
  --sand:#faf6ee; --sand2:#f3ead4; --bandink:#f4ecda;
  --ink:#21303a; --muted:#5b6b73; --line:#e7ddc9;
  --sline:#e7e2d6; --smuted:#6b7772; --sgood:#2e7d5b; --scoral:#b24e34; --sink:#1f2d36;
  --serif:'Playfair Display', Georgia, 'Times New Roman', serif;
  --sans:Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  --mono:ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  position:fixed; inset:0; overflow:hidden;
  background:var(--navy950); color:var(--bandink);
  font-family:var(--sans);
}
.gvs-root *, .gvs-root *::before, .gvs-root *::after { box-sizing:border-box; }
.gvs-root h1,.gvs-root h2,.gvs-root h3,.gvs-root h4 { font-family:var(--serif); font-weight:700; margin:0; text-wrap:balance; }
.gvs-root p { margin:0; text-wrap:pretty; }

.gvs-stage {
  position:absolute; inset:0; display:grid; place-items:center;
  background:radial-gradient(ellipse at 50% 42%, #143349 0%, var(--navy950) 72%);
  touch-action:pan-y;
}
.gvs-deck {
  width:1440px; height:810px; position:relative; flex:none; transform-origin:center center;
  box-shadow:0 34px 90px rgba(0,0,0,.55);
}
.gvs-slide { position:absolute; inset:0; opacity:0; visibility:hidden; transition:opacity .24s ease; }
.gvs-slide.on { opacity:1; visibility:visible; }

/* ---- frames ---- */
.gvs-frame { width:100%; height:100%; display:flex; flex-direction:column; overflow:hidden; }
.gvs-frame.sand { background:var(--sand); color:var(--ink); }
.gvs-frame.navy { background:linear-gradient(180deg, var(--navy8) 0%, var(--navy7) 55%, var(--navy6) 100%); border-bottom:6px solid var(--gold); }

.gvs-mark {
  width:32px; height:32px; border-radius:9px; flex:none; display:grid; place-items:center;
  background:linear-gradient(150deg, var(--gold4), var(--gold6));
  color:#17324a; font-family:var(--serif); font-weight:700; font-size:16px;
}
.gvs-brandname { font-size:15px; letter-spacing:.22em; text-transform:uppercase; font-weight:700; color:var(--muted); }
.gvs-brandname.big { color:rgba(244,236,218,.82); font-size:16px; letter-spacing:.24em; }
.gvs-eyebrow { font-size:15px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; }
.gvs-eyebrow.gold { color:var(--gold4); }

.gvs-chrome { display:flex; align-items:center; gap:15px; padding:44px 80px 0; flex:none; }
.gvs-chrome.light .gvs-brandname { color:rgba(244,236,218,.72); }
.gvs-chrome-r { margin-left:auto; display:flex; align-items:center; gap:18px; }
.gvs-section { font-size:15px; letter-spacing:.16em; text-transform:uppercase; font-weight:700; }
.gvs-count { font-family:var(--mono); font-size:15px; color:#93a0a3; font-variant-numeric:tabular-nums; white-space:nowrap; }
.gvs-chrome.light .gvs-count { color:rgba(244,236,218,.55); }

/* cover */
.gvs-coverin { flex-grow:1; display:flex; flex-direction:column; justify-content:center; gap:30px; padding:0 100px; }
.gvs-brandline { display:flex; align-items:center; gap:16px; }
.gvs-frame.cover h1 { font-size:92px; line-height:1.02; color:var(--bandink); max-width:15ch; letter-spacing:-.015em; }
.gvs-lede { font-size:25px; line-height:1.5; color:rgba(244,236,218,.86); max-width:60ch; }
.gvs-legend { display:flex; flex-wrap:wrap; gap:12px; margin-top:8px; }
.gvs-role { display:inline-flex; align-items:center; gap:11px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18); padding:11px 19px; border-radius:999px; font-size:18px; font-weight:600; color:var(--bandink); }
.gvs-role i { width:12px; height:12px; border-radius:50%; flex:none; }
.gvs-coverfoot { display:flex; align-items:center; gap:14px; padding:0 100px 46px; font-size:16px; color:rgba(244,236,218,.58); flex:none; }
.gvs-dot { color:rgba(244,236,218,.28); }

/* roles */
.gvs-rolesbody { flex-grow:1; display:flex; flex-direction:column; justify-content:center; gap:34px; padding:0 80px; }
.gvs-rolehead { display:flex; flex-direction:column; gap:16px; }
.gvs-rolehead h2 { font-size:52px; line-height:1.08; color:var(--navy7); }
.gvs-rolehead p { font-size:21px; line-height:1.55; color:var(--muted); max-width:78ch; }
.gvs-rolegrid { display:flex; gap:24px; align-items:stretch; }
.gvs-rolecard { flex:1 1 0; background:#fff; border:1px solid var(--line); border-radius:16px; padding:28px 26px; display:flex; flex-direction:column; gap:16px; box-shadow:0 1px 3px rgba(28,63,85,.05); }
.gvs-rolebar { width:44px; height:6px; border-radius:999px; flex:none; }
.gvs-rolecard h3 { font-size:27px; color:var(--navy7); }
.gvs-rolelines { display:flex; flex-direction:column; gap:11px; }
.gvs-roleline { display:flex; gap:11px; align-items:flex-start; font-size:19px; line-height:1.45; color:var(--muted); }
.gvs-roleline svg { flex:none; margin-top:3px; }
.gvs-tip { background:var(--sand2); border-left:4px solid var(--gold6); border-radius:10px; padding:15px 19px; font-size:18px; line-height:1.5; color:var(--ink); }
.gvs-tip b { color:var(--gold6); }

/* divider */
.gvs-divbody { flex-grow:1; display:flex; align-items:center; gap:80px; padding:0 80px; }
.gvs-divtext { flex-grow:1; display:flex; flex-direction:column; gap:26px; align-items:flex-start; }
.gvs-divtext h2 { font-size:74px; line-height:1.05; color:var(--bandink); max-width:16ch; }
.gvs-divtext p { font-size:23px; line-height:1.55; color:rgba(244,236,218,.82); max-width:56ch; }
.gvs-scenes { display:flex; flex-wrap:wrap; gap:11px; margin-top:6px; }
.gvs-scene { display:inline-flex; align-items:center; gap:10px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); padding:9px 17px; border-radius:999px; font-size:17px; font-weight:600; color:var(--bandink); }
.gvs-scene i { width:7px; height:7px; border-radius:50%; flex:none; }
.gvs-divnum { flex:none; font-family:var(--serif); font-weight:700; font-size:250px; line-height:.8; color:rgba(244,236,218,.10); letter-spacing:-.03em; }

/* rules */
.gvs-rulesbody { flex-grow:1; display:flex; flex-direction:column; justify-content:center; gap:34px; padding:0 80px; }
.gvs-ruleshead { display:flex; flex-direction:column; gap:18px; }
.gvs-ruleshead h2 { font-size:58px; line-height:1.06; color:var(--bandink); max-width:26ch; }
.gvs-ruleshead p { font-size:22px; line-height:1.55; color:rgba(244,236,218,.84); max-width:82ch; }
.gvs-flow { display:flex; gap:20px; align-items:stretch; }
.gvs-fstep { flex:1 1 0; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:26px 24px; display:flex; flex-direction:column; gap:12px; }
.gvs-fn { font-family:var(--serif); font-size:17px; color:var(--gold4); font-weight:700; }
.gvs-fstep h4 { font-size:25px; color:var(--bandink); }
.gvs-fstep p { font-size:18px; line-height:1.5; color:rgba(244,236,218,.76); }

/* cta */
.gvs-ctabody { flex-grow:1; display:flex; align-items:center; gap:90px; padding:0 80px; }
.gvs-ctaleft { width:640px; flex:none; display:flex; flex-direction:column; gap:26px; align-items:flex-start; }
.gvs-ctaleft h2 { font-size:70px; line-height:1.04; color:var(--bandink); max-width:13ch; }
.gvs-ctaleft p { font-size:22px; line-height:1.55; color:rgba(244,236,218,.84); }
.gvs-ctabtn { display:inline-flex; align-items:center; gap:12px; background:var(--gold); color:#17324a; padding:15px 26px; border-radius:12px; font-size:20px; font-weight:700; text-decoration:none; transition:background .15s ease; }
.gvs-ctabtn:hover { background:var(--gold4); }
.gvs-ctasteps { flex-grow:1; display:flex; flex-direction:column; gap:28px; }
.gvs-ctastep { display:flex; gap:18px; align-items:flex-start; }
.gvs-ctan { width:44px; height:44px; border-radius:50%; border:2px solid var(--gold4); color:var(--gold4); font-family:var(--serif); font-weight:700; font-size:19px; display:grid; place-items:center; flex:none; }
.gvs-ctatext { display:flex; flex-direction:column; gap:5px; }
.gvs-ctatext b { font-size:21px; font-weight:600; color:var(--bandink); }
.gvs-ctatext span { font-size:18px; line-height:1.45; color:rgba(244,236,218,.68); }

/* scene */
.gvs-scenebody { flex-grow:1; display:flex; align-items:center; gap:76px; padding:0 80px; }
.gvs-scenetext { width:516px; flex:none; display:flex; flex-direction:column; gap:22px; align-items:flex-start; }
.gvs-scenetext h2 { font-size:45px; line-height:1.1; color:var(--navy7); }
.gvs-scenetext p { font-size:21px; line-height:1.58; color:var(--muted); }
.gvs-scenemock { width:688px; flex:none; }
.gvs-progress { display:flex; height:6px; flex:none; background:var(--line); }
.gvs-progress span { display:block; height:100%; }

/* ---- screen mockup ---- */
.gvs-screen { background:#fff; color:var(--sink); border-radius:18px; border:1px solid rgba(28,63,85,.14); box-shadow:0 22px 48px rgba(28,63,85,.16), 0 3px 10px rgba(28,63,85,.10); overflow:hidden; }
.gvs-bar { display:flex; align-items:center; gap:13px; background:var(--navy7); color:#eaf1f6; padding:13px 19px; }
.gvs-bmark { width:24px; height:24px; border-radius:6px; background:linear-gradient(150deg, var(--gold4), var(--gold6)); flex:none; }
.gvs-bname { letter-spacing:.12em; text-transform:uppercase; font-weight:700; opacity:.92; font-size:15px; }
.gvs-btab { margin-left:auto; background:rgba(255,255,255,.18); padding:5px 15px; border-radius:999px; font-weight:600; font-size:15px; }
.gvs-sbody { padding:24px; background:#f6f2ea; font-size:20px; line-height:1.45; }
.gvs-stitle { font-family:var(--serif); font-size:25px; font-weight:700; color:var(--navy7); margin-bottom:18px; }
.gvs-sstack { display:flex; flex-direction:column; gap:16px; }

.gvs-card { background:#fff; border:1px solid var(--sline); border-radius:14px; padding:18px 19px; display:flex; flex-direction:column; gap:14px; }
.gvs-field { display:flex; flex-direction:column; gap:6px; }
.gvs-flabel { font-size:16px; color:var(--smuted); text-transform:uppercase; letter-spacing:.05em; font-weight:700; }
.gvs-fbox { display:flex; align-items:center; justify-content:space-between; gap:12px; background:#fff; border:1px solid var(--sline); border-radius:11px; padding:13px 16px; color:var(--sink); }
.gvs-fbox.ph { color:var(--smuted); }
.gvs-chev { color:var(--smuted); flex:none; }
.gvs-grid2 { display:grid; grid-template-columns:repeat(2, minmax(0,1fr)); gap:14px; }
.gvs-stat { background:#fff; border:1px solid var(--sline); border-radius:14px; padding:16px 17px; }
.gvs-stat .gvs-k { font-size:15px; color:var(--smuted); text-transform:uppercase; letter-spacing:.05em; font-weight:700; }
.gvs-v { font-family:var(--serif); font-weight:700; font-size:29px; margin-top:5px; color:var(--sink); }
.gvs-v.pos { color:var(--sgood); } .gvs-v.neg { color:var(--scoral); }

.gvs-row { display:flex; align-items:center; justify-content:space-between; gap:14px; padding-bottom:13px; border-bottom:1px solid var(--sline); }
.gvs-row.last { padding-bottom:0; border-bottom:0; }
.gvs-rl { display:flex; flex-direction:column; gap:3px; }
.gvs-rr { display:flex; flex-direction:column; gap:5px; align-items:flex-end; }
.gvs-lbl { font-weight:600; color:var(--sink); }
.gvs-sub { font-size:17px; color:var(--smuted); }
.gvs-recargo { font-size:17px; color:var(--scoral); font-weight:600; }
.gvs-num { font-variant-numeric:tabular-nums; font-weight:700; color:var(--sink); }
.gvs-doc { display:flex; align-items:center; gap:12px; font-weight:600; color:var(--sink); }
.gvs-doc svg { color:var(--navy7); flex:none; }
.gvs-kv { display:flex; justify-content:space-between; align-items:center; gap:14px; }
.gvs-kv .gvs-k { color:var(--smuted); }
.gvs-total { display:flex; align-items:center; gap:10px; color:var(--sgood); font-weight:700; font-size:19px; }
.gvs-restitle { font-weight:700; color:var(--sink); font-size:21px; }
.gvs-tally { display:flex; gap:22px; font-weight:700; font-size:19px; }
.gvs-tally .y { color:var(--sgood); } .gvs-tally .n { color:var(--scoral); } .gvs-tally .a { color:var(--smuted); }
.gvs-upload { display:flex; align-items:center; gap:12px; background:#fff; border:1px solid var(--sline); border-radius:14px; padding:16px 18px; color:var(--sink); }
.gvs-upload svg { color:var(--navy7); flex:none; }
.gvs-net { font-family:var(--serif); font-weight:700; font-size:25px; color:var(--sgood); }
.gvs-search { display:flex; align-items:center; gap:12px; background:#fff; border:1px solid var(--sline); border-radius:12px; padding:14px 17px; color:var(--smuted); }
.gvs-search svg { flex:none; }
.gvs-q { color:var(--sink); font-weight:600; }

.gvs-badge { font-size:15px; font-weight:700; padding:4px 13px; border-radius:999px; display:inline-block; white-space:nowrap; }
.gvs-badge.warn { background:#fbedcb; color:#8a5a12; }
.gvs-badge.info { background:#dceaf2; color:#2c5c74; }
.gvs-badge.ok { background:#d8eee0; color:#1f6b48; }
.gvs-badge.danger { background:#f7ded6; color:#9a3a22; }
.gvs-badge.gold { background:#f4e4b8; color:#7a5410; }

.gvs-btn { display:inline-flex; align-items:center; gap:9px; font-size:18px; font-weight:700; padding:11px 21px; border-radius:13px; border:1px solid transparent; }
.gvs-btn.gold { background:var(--gold6); color:#fff; border-color:var(--gold6); }
.gvs-btn.sea { background:var(--navy7); color:#fff; border-color:var(--navy7); }
.gvs-btn.good { background:var(--sgood); color:#fff; border-color:var(--sgood); }
.gvs-btn.ghost { background:transparent; border-color:var(--sline); color:var(--sink); }
.gvs-btn.reject { background:transparent; border-color:#e6c3b8; color:var(--scoral); }
.gvs-btnrow { display:flex; gap:13px; flex-wrap:wrap; }

.gvs-rulep { background:#fbf3de; border:1px solid #e7ce85; border-radius:14px; padding:17px 19px; }
.gvs-rphead { display:flex; align-items:center; gap:11px; font-weight:700; color:#8a5a12; font-size:18px; }
.gvs-rphead svg { flex:none; }
.gvs-rpcount { background:#ebd48a; color:#6b4a0f; border-radius:999px; padding:2px 12px; font-size:15px; font-weight:700; }
.gvs-rprule { display:flex; flex-direction:column; gap:6px; align-items:flex-start; margin-top:14px; }
.gvs-rprule.sep { border-top:1px dashed #e7ce85; padding-top:13px; }
.gvs-rptitle { font-weight:700; font-size:18px; color:var(--sink); }
.gvs-rpbody { color:#6c6355; font-size:17px; }
.gvs-cite { font-family:var(--mono); font-size:15px; background:#efece0; color:#6b5f45; padding:3px 10px; border-radius:7px; }

/* ---- deck chrome ---- */
.gvs-rail { position:absolute; left:0; right:0; bottom:0; height:3px; background:rgba(244,236,218,.12); z-index:20; }
.gvs-rail span { display:block; height:100%; background:var(--gold); transition:width .24s ease; }

.gvs-top { position:absolute; top:20px; left:20px; right:20px; display:flex; justify-content:space-between; gap:12px; z-index:30; }
.gvs-pillbtn {
  display:inline-flex; align-items:center; gap:8px; padding:9px 16px; border-radius:999px;
  background:rgba(9,24,35,.72); border:1px solid rgba(244,236,218,.18); color:var(--bandink);
  font:inherit; font-size:14px; font-weight:600; cursor:pointer; text-decoration:none;
  backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); transition:background .15s ease;
}
.gvs-pillbtn:hover { background:rgba(244,236,218,.16); }

.gvs-ctl {
  position:absolute; bottom:26px; left:50%; transform:translateX(-50%);
  display:flex; align-items:center; gap:6px; z-index:30; padding:7px 9px; border-radius:999px;
  background:rgba(9,24,35,.72); border:1px solid rgba(244,236,218,.18);
  backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.gvs-ctl button {
  display:grid; place-items:center; width:38px; height:38px; padding:0; border:0; border-radius:50%;
  background:transparent; color:var(--bandink); cursor:pointer; transition:background .14s ease;
}
.gvs-ctl button:hover:not(:disabled) { background:rgba(244,236,218,.13); }
.gvs-ctl button:disabled { opacity:.32; cursor:default; }
.gvs-ctlcount { font-family:var(--mono); font-size:14px; font-variant-numeric:tabular-nums; letter-spacing:.06em; color:rgba(244,236,218,.82); padding:0 10px; min-width:74px; text-align:center; white-space:nowrap; }
.gvs-sep { width:1px; height:22px; background:rgba(244,236,218,.18); flex:none; }
.gvs-root button:focus-visible, .gvs-root a:focus-visible { outline:2px solid var(--gold); outline-offset:2px; }

.gvs-index { position:absolute; inset:0; z-index:40; display:flex; flex-direction:column; gap:22px; padding:32px clamp(20px,5vw,72px); overflow-y:auto; background:rgba(8,21,31,.96); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px); }
.gvs-indexhead { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.gvs-indexhead h2 { font-size:30px; color:var(--bandink); }
.gvs-indexhead button { background:transparent; border:0; color:var(--bandink); cursor:pointer; padding:8px; border-radius:50%; display:grid; place-items:center; }
.gvs-indexhead button:hover { background:rgba(244,236,218,.13); }
.gvs-indexgrid { display:grid; grid-template-columns:repeat(auto-fill, minmax(258px, 1fr)); gap:10px; }
.gvs-ix { display:flex; align-items:center; gap:13px; text-align:left; cursor:pointer; padding:13px 16px; border-radius:11px; color:var(--bandink); background:rgba(244,236,218,.05); border:1px solid rgba(244,236,218,.16); font:inherit; font-size:15px; transition:background .14s ease, border-color .14s ease; }
.gvs-ix:hover { background:rgba(244,236,218,.11); border-color:rgba(244,236,218,.3); }
.gvs-ix.here { border-color:var(--gold); background:rgba(200,165,96,.14); }
.gvs-ix i { width:9px; height:9px; border-radius:50%; flex:none; }
.gvs-ixn { font-family:var(--mono); font-size:13px; color:rgba(244,236,218,.5); font-variant-numeric:tabular-nums; }
.gvs-ixt { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* ---- narrow screens: reflow instead of shrink ---- */
/* a centering grid track sizes to max-content, which lets the 1440px layout push
   past a phone viewport — on narrow screens the deck is a plain full-width block. */
.gvs-root.narrow .gvs-stage { display:block; }
.gvs-root.narrow .gvs-deck { width:100%; height:100%; max-width:100%; transform:none; box-shadow:none; }
.gvs-root.narrow .gvs-slide { overflow-y:auto; overflow-x:hidden; -webkit-overflow-scrolling:touch; }
.gvs-root.narrow .gvs-slide *, .gvs-root.narrow .gvs-frame { min-width:0; max-width:100%; }
.gvs-root.narrow .gvs-count, .gvs-root.narrow .gvs-ctlcount { max-width:none; }
.gvs-root.narrow .gvs-screen, .gvs-root.narrow .gvs-card, .gvs-root.narrow .gvs-rulep { overflow-wrap:anywhere; }
.gvs-root.narrow .gvs-chrome { padding:128px 20px 0; gap:10px; }
.gvs-root.narrow .gvs-brandname { font-size:11px; letter-spacing:.16em; }
.gvs-root.narrow .gvs-chrome-r { gap:10px; }
.gvs-root.narrow .gvs-section { font-size:11px; }
.gvs-root.narrow .gvs-count { font-size:12px; }
.gvs-root.narrow .gvs-eyebrow { font-size:12px; }

.gvs-root.narrow .gvs-scenebody { flex-direction:column; align-items:stretch; justify-content:flex-start; gap:22px; padding:22px 20px 90px; }
.gvs-root.narrow .gvs-scenetext { width:auto; gap:14px; }
.gvs-root.narrow .gvs-scenetext h2 { font-size:clamp(24px,6.4vw,32px); }
.gvs-root.narrow .gvs-scenetext p { font-size:16px; line-height:1.55; }
.gvs-root.narrow .gvs-tip { font-size:14px; padding:12px 15px; }
.gvs-root.narrow .gvs-scenemock { width:auto; }

.gvs-root.narrow .gvs-coverin { padding:22px 20px; gap:18px; }
.gvs-root.narrow .gvs-frame.cover h1 { font-size:clamp(32px,9vw,48px); }
.gvs-root.narrow .gvs-lede { font-size:16px; }
.gvs-root.narrow .gvs-role { font-size:14px; padding:8px 14px; }
.gvs-root.narrow .gvs-coverfoot { padding:0 20px 92px; font-size:13px; flex-wrap:wrap; }

.gvs-root.narrow .gvs-divbody { flex-direction:column; align-items:flex-start; justify-content:center; gap:18px; padding:22px 20px 90px; }
.gvs-root.narrow .gvs-divtext { gap:16px; }
.gvs-root.narrow .gvs-divtext h2 { font-size:clamp(30px,8vw,44px); }
.gvs-root.narrow .gvs-divtext p { font-size:16px; }
.gvs-root.narrow .gvs-scene { font-size:14px; padding:7px 13px; }
.gvs-root.narrow .gvs-divnum { display:none; }

.gvs-root.narrow .gvs-rolesbody,
.gvs-root.narrow .gvs-rulesbody { padding:22px 20px 90px; gap:20px; justify-content:flex-start; }
.gvs-root.narrow .gvs-rolehead h2 { font-size:clamp(26px,7vw,36px); }
.gvs-root.narrow .gvs-rolehead p { font-size:16px; }
.gvs-root.narrow .gvs-rolegrid { flex-direction:column; gap:14px; }
.gvs-root.narrow .gvs-rolecard { padding:20px 18px; gap:12px; }
.gvs-root.narrow .gvs-rolecard h3 { font-size:21px; }
.gvs-root.narrow .gvs-roleline { font-size:15px; }
.gvs-root.narrow .gvs-ruleshead h2 { font-size:clamp(26px,7vw,38px); }
.gvs-root.narrow .gvs-ruleshead p { font-size:16px; }
.gvs-root.narrow .gvs-flow { flex-direction:column; gap:12px; }
.gvs-root.narrow .gvs-fstep { padding:18px 18px; gap:8px; }
.gvs-root.narrow .gvs-fstep h4 { font-size:19px; }
.gvs-root.narrow .gvs-fstep p { font-size:15px; }

.gvs-root.narrow .gvs-ctabody { flex-direction:column; align-items:flex-start; justify-content:flex-start; gap:26px; padding:22px 20px 40px; }
.gvs-root.narrow .gvs-ctaleft { width:auto; gap:16px; }
.gvs-root.narrow .gvs-ctaleft h2 { font-size:clamp(30px,8vw,44px); }
.gvs-root.narrow .gvs-ctaleft p { font-size:16px; }
.gvs-root.narrow .gvs-ctasteps { gap:18px; }
.gvs-root.narrow .gvs-ctan { width:34px; height:34px; font-size:15px; }
.gvs-root.narrow .gvs-ctatext b { font-size:17px; }
.gvs-root.narrow .gvs-ctatext span { font-size:15px; }

.gvs-root.narrow .gvs-sbody { padding:16px; font-size:15px; }
.gvs-root.narrow .gvs-stitle { font-size:19px; margin-bottom:12px; }
.gvs-root.narrow .gvs-sstack { gap:12px; }
.gvs-root.narrow .gvs-bname { font-size:11px; }
.gvs-root.narrow .gvs-btab { font-size:12px; padding:4px 11px; }
.gvs-root.narrow .gvs-flabel { font-size:12px; }
.gvs-root.narrow .gvs-fbox { padding:10px 12px; border-radius:9px; }
.gvs-root.narrow .gvs-v { font-size:22px; }
.gvs-root.narrow .gvs-stat .gvs-k { font-size:12px; }
.gvs-root.narrow .gvs-badge { font-size:12px; padding:3px 10px; }
.gvs-root.narrow .gvs-btn { font-size:14px; padding:9px 15px; border-radius:10px; }
.gvs-root.narrow .gvs-sub, .gvs-root.narrow .gvs-recargo { font-size:13px; }
.gvs-root.narrow .gvs-rptitle, .gvs-root.narrow .gvs-rphead { font-size:14px; }
.gvs-root.narrow .gvs-rpbody { font-size:13px; }
.gvs-root.narrow .gvs-cite { font-size:12px; }
.gvs-root.narrow .gvs-total, .gvs-root.narrow .gvs-tally, .gvs-root.narrow .gvs-restitle { font-size:15px; }
.gvs-root.narrow .gvs-net { font-size:19px; }
.gvs-root.narrow .gvs-progress { display:none; }
/* Moved to a second row under the back/language pills, not the bottom: in
   narrow mode a slide scrolls, and a bottom-fixed bar would permanently sit
   on top of whatever content happened to be scrolled underneath it. */
.gvs-root.narrow .gvs-ctl { top:64px; bottom:auto; }
.gvs-root.narrow .gvs-ctlcount { min-width:68px; font-size:13px; padding:0 4px; }

@media (prefers-reduced-motion: reduce) {
  .gvs-slide, .gvs-rail span, .gvs-ctl button, .gvs-ix, .gvs-pillbtn { transition:none; }
}
`;
